
import { useState, useEffect } from 'react';
import { Globe2, HeartHandshake, MessageCircle, Sprout } from 'lucide-react';
import SelectWhatsappModal from '../../SelectWhatsappModal';

const rotatingWords = [
  'story', 'perspective', 'companionship', 'guidance', 'listening',
  'curiosity', 'warmth', 'patience', 'faith', 'wisdom',
  'humor', 'hope', 'reflection', 'friendship', 'common ground'
];

export default function Hero({ logoSrc = '/boggler-logo.webp', guideImages, onApply }) {
  const [rotatingWord, setRotatingWord] = useState('story');
  const [showSelectWhatsappModal, setShowSelectWhatsappModal] = useState(false);

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % rotatingWords.length;
      setRotatingWord(rotatingWords[currentIndex] ?? 'story');
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const defaultGuideImages = [
    { src: '/guide-cyber-monk.png', alt: 'Cyber Monk guide archetype' },
    { src: '/guide-peer-sister.png', alt: 'Peer Sister guide archetype' },
    { src: '/guide-peer-brother.png', alt: 'Peer Brother guide archetype' },
  ];

  const images = guideImages || defaultGuideImages;

  return (
    <>
      <SelectWhatsappModal show={showSelectWhatsappModal} onClose={() => setShowSelectWhatsappModal(false)} />
      <section className="relative min-h-screen flex items-center justify-center px-6 py-20">
        <div className="absolute inset-0 bg-gradient-radial from-warm-beige via-light-warm to-warm-cream opacity-60" />

        <div className="relative z-10 max-w-6xl mx-auto text-center">

          <h1 className="lucky-font text-5xl md:text-7xl text-dark-teal mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Find Someone to Talk To
          </h1>

          <p className="text-2xl md:text-3xl text-secondary-text mb-8 font-lato animate-fade-in-up max-w-4xl mx-auto" style={{ animationDelay: '0.4s' }}>
            The Boggler introduces people to donation-based mental health guides for private WhatsApp conversations.
            <br />
            Share <span className="text-boggler-teal font-bold transition-all duration-500">{rotatingWord}</span>, hear another life, and make room for a real one-on-one connection.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-12 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-card">
              <MessageCircle className="w-6 h-6 text-whatsapp-green" aria-hidden="true" />
              <span className="font-montserrat text-primary-text">Private WhatsApp talks</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-card">
              <HeartHandshake className="w-6 h-6 text-golden-yellow" aria-hidden="true" />
              <span className="font-montserrat text-primary-text">Donation based</span>
            </div>
            <div className="flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-card">
              <Globe2 className="w-6 h-6 text-boggler-teal" aria-hidden="true" />
              <span className="font-montserrat text-primary-text">Guides around the world</span>
            </div>
          </div>

          <button
            onClick={() => setShowSelectWhatsappModal(true)}
            className="whatsapp-button bg-whatsapp-green text-white px-12 py-5 rounded-full font-montserrat font-semibold shadow-boggler btn-hover-lift inline-flex items-center gap-3 animate-fade-in-up"
            style={{ animationDelay: '0.8s' }}
          >
            <Sprout className="w-7 h-7" aria-hidden="true" />
            Become a Guide
          </button>

          <p className="mt-4 text-muted-text font-lato animate-fade-in-up" style={{ animationDelay: '1s' }}>
            For life coaches, spiritual or religious devotees, retirees, elders, and digital friends willing to listen.
          </p>

          <div className="mt-16 flex justify-center gap-8 flex-wrap">
            {images.map((img, i) => (
              <div key={i} className="animate-fade-in-up" style={{ animationDelay: `${1.2 + i * 0.2}s` }}>
                <img src={img.src} alt={img.alt} className="default-guide-images object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
